// Auth context: bootstraps the user-session via GET /api/v1/auth/me, exposes
// login (SSO + password) and logout, and the admin flag for view gating.

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, ApiError, setSessionToken, onSessionEnded } from '../api/client';
import { apiUrl } from '../config';
import type { AuthMe, User } from '../api/schemas';

export type RegisterOutcome = { verificationRequired: boolean; email?: string };

interface AuthState {
  loading: boolean;
  user: User | null;
  me: AuthMe | null;
  isAdmin: boolean;
  error: string | null;
  loginWithPassword: (email: string, password: string) => Promise<void>;
  /** EV1: resolves `{ verificationRequired: true, email }` when the account must confirm its
   *  address first (no session was opened), else signs in and resolves `{ verificationRequired: false }`. */
  registerWithPassword: (email: string, password: string, displayName?: string) => Promise<RegisterOutcome>;
  loginWithSso: (provider: 'gitlab' | 'google') => void;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState<AuthMe | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const result = await api.me();
      setMe(result);
      setError(null);
    } catch (e) {
      // 401 is the normal logged-out path — not an error to surface.
      if (!(e instanceof ApiError && e.status === 401)) {
        setError(e instanceof Error ? e.message : 'failed to load session');
      }
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // AH8 — a sign-out that happened on ANOTHER surface has to land here too. The session this tab's
  // cookie names is revoked by the workspace's sign-out, so every call starts answering 401; that
  // answer is the notice. Dropping `me` routes the app to the sign-in screen instead of leaving a
  // signed-in shell whose panels all quietly fail, which is what made "sign out" look one-sided.
  useEffect(() => onSessionEnded(() => {
    setMe(null);
    setLoading(false);
  }), []);

  const loginWithPassword = useCallback(
    async (email: string, password: string) => {
      setError(null);
      const res = await api.login(email, password);
      // Prefer cookie auth; if a bearer is returned, keep it in memory.
      if (res.token) setSessionToken(res.token);
      await refresh();
    },
    [refresh],
  );

  const registerWithPassword = useCallback(
    async (email: string, password: string, displayName?: string) => {
      setError(null);
      // On success the server sets the session cookie (and may return a bearer);
      // refresh() then loads /auth/me so the app routes into the dashboard.
      const res = await api.register(email, password, displayName);
      if ('verification_required' in res) return { verificationRequired: true, email: res.email };
      if (res.token) setSessionToken(res.token);
      await refresh();
      return { verificationRequired: false };
    },
    [refresh],
  );

  const loginWithSso = useCallback((provider: 'gitlab' | 'google') => {
    // Full-page redirect to the server SSO entry; it 302s back after callback.
    window.location.href = apiUrl(`/api/v1/auth/sso/${provider}`);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setSessionToken(null);
      setMe(null);
    }
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      loading,
      user: me?.user ?? null,
      me,
      isAdmin: me?.user.role === 'admin',
      error,
      loginWithPassword,
      registerWithPassword,
      loginWithSso,
      logout,
      refresh,
    }),
    [loading, me, error, loginWithPassword, registerWithPassword, loginWithSso, logout, refresh],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
